import { useCallback, useEffect, useMemo, useState } from "react";

import { SectionCard } from "@/components/common/SectionCard";
import { ghostBtnClass } from "@/components/manufacturing/ManufacturingOrderIntakeShared";
import {
  DealerOrderResubmitForm,
  type DealerResubmitDraft,
} from "@/components/dealer/DealerOrderResubmitForm";
import {
  dealerCreateOrderIntake,
  listDealerOrderIntakes,
  type OpsOrderIntakeRow,
  type OpsOrderIntakeSalePath,
  type OpsOrderIntakeStatus,
} from "@/lib/orderIntakeClient";
import { computeDealerResubmitDraftDiff } from "@/lib/orderIntakePayloadDiff";
import { fetchOrgLocations, type OrgLocationRow } from "@/lib/locations/fetchOrgLocations";
import type { OrderManufacturerPartner } from "@/lib/sales-intake/manufacturerSubmissionRouting";
import { formatSalesRepOrderRowLabel } from "@/lib/sales-intake/salesRepOrderDisplayLabel";
import { getOpsSettings } from "@/lib/opsPaperworkClientPartB";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";

const PAGE_SIZE = 10;
const OPEN_STATUSES: OpsOrderIntakeStatus[] = ["pending_review", "on_hold"];

type ViewMode = "open" | "all";

function parseManufacturerPartners(raw: unknown): OrderManufacturerPartner[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((x) => x && typeof x === "object")
    .map((x) => {
      const row = x as { id?: unknown; name?: unknown; on_platform?: unknown };
      const id = typeof row.id === "string" ? row.id.trim() : "";
      if (!id) return null;
      return {
        id,
        name: typeof row.name === "string" && row.name.trim() ? row.name.trim() : id,
        on_platform: Boolean(row.on_platform),
      };
    })
    .filter((x): x is OrderManufacturerPartner => x !== null);
}

/** OI-5b / OI-6b dealer half — Order History + Revise & Resubmit. */
export function DealerOrderHistoryPanel() {
  const auth = useAuth();
  const orgId = auth.activeOrganizationId;
  const [rows, setRows] = useState<OpsOrderIntakeRow[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [page, setPage] = useState(0);
  const [viewMode, setViewMode] = useState<ViewMode>("open");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [manufacturers, setManufacturers] = useState<OrderManufacturerPartner[]>([]);
  const [locations, setLocations] = useState<OrgLocationRow[]>([]);
  const [selected, setSelected] = useState<OpsOrderIntakeRow | null>(null);
  const [resubmitDraft, setResubmitDraft] = useState<DealerResubmitDraft | null>(null);
  const [resubmitBusy, setResubmitBusy] = useState(false);
  const [resubmitError, setResubmitError] = useState<string | null>(null);
  const [resubmitSuccess, setResubmitSuccess] = useState<string | null>(null);

  const manufacturerNameByOrgId = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of manufacturers) m.set(p.id, p.name);
    return m;
  }, [manufacturers]);

  const loadCatalog = useCallback(async () => {
    if (!orgId || !supabase) return;
    const [settingsRes, locRes] = await Promise.all([
      getOpsSettings(),
      fetchOrgLocations(supabase, orgId),
    ]);
    if (settingsRes.ok) {
      setManufacturers(parseManufacturerPartners(settingsRes.data.manufacturer_options));
    }
    if (!locRes.error) setLocations(locRes.data);
  }, [orgId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const r = await listDealerOrderIntakes({
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
      ...(viewMode === "open" ? { status: OPEN_STATUSES } : {}),
    });
    setLoading(false);
    if (!r.ok) {
      setRows([]);
      setTotal(null);
      setError(r.error);
      return;
    }
    setRows(r.data.rows ?? []);
    setTotal(r.data.total);
  }, [page, viewMode]);

  useEffect(() => {
    void loadCatalog();
  }, [loadCatalog]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setPage(0);
  }, [viewMode]);

  const openResubmit = (row: OpsOrderIntakeRow) => {
    setSelected(row);
    setResubmitError(null);
    setResubmitSuccess(null);
    setResubmitDraft({
      salePath: (row.proposed_sale_path as OpsOrderIntakeSalePath) || "",
      sellerLocationId: row.proposed_seller_location_id || "",
      endCustomerContactId: row.proposed_end_customer_contact_id || "",
      notes: "",
    });
  };

  const liveDiff = useMemo(() => {
    if (!selected || !resubmitDraft) return null;
    return computeDealerResubmitDraftDiff(selected, {
      proposed_seller_location_id: resubmitDraft.sellerLocationId || null,
      proposed_sale_path: resubmitDraft.salePath || null,
      proposed_end_customer_contact_id: resubmitDraft.endCustomerContactId || null,
      proposed_model_id: selected.proposed_model_id,
      proposed_options: selected.proposed_options ?? {},
      raw_payload: {
        ...(selected.raw_payload ?? {}),
        ...(resubmitDraft.notes.trim() ? { notes: resubmitDraft.notes.trim() } : {}),
      },
    });
  }, [selected, resubmitDraft]);

  const submitResubmit = async () => {
    if (!selected || !resubmitDraft) return;
    if (!resubmitDraft.salePath) {
      setResubmitError("Sale path is required.");
      return;
    }
    if (!resubmitDraft.sellerLocationId.trim()) {
      setResubmitError("Seller location is required.");
      return;
    }
    if (!resubmitDraft.endCustomerContactId.trim()) {
      setResubmitError("End customer contact id is required.");
      return;
    }
    setResubmitBusy(true);
    setResubmitError(null);
    setResubmitSuccess(null);
    const raw_payload: Record<string, unknown> = {
      ...(selected.raw_payload ?? {}),
      submission_channel: "dealer_portal_resubmit",
    };
    if (resubmitDraft.notes.trim()) raw_payload.notes = resubmitDraft.notes.trim();
    const r = await dealerCreateOrderIntake({
      target_manufacturer_org_id: selected.organization_id,
      proposed_seller_location_id: resubmitDraft.sellerLocationId.trim(),
      proposed_sale_path: resubmitDraft.salePath,
      proposed_end_customer_contact_id: resubmitDraft.endCustomerContactId.trim(),
      proposed_model_id: selected.proposed_model_id,
      proposed_options: selected.proposed_options ?? {},
      raw_payload,
      resubmit_intake_id: selected.id,
    });
    setResubmitBusy(false);
    if (!r.ok) {
      setResubmitError(r.error);
      return;
    }
    setResubmitSuccess(
      r.data.intake.merged
        ? "Resubmitted — pending manufacturer review again."
        : "Submitted."
    );
    setSelected(r.data.intake);
    setResubmitDraft(null);
    void load();
  };

  const pageCount =
    total != null && total > 0 ? Math.ceil(total / PAGE_SIZE) : rows.length > 0 ? page + 1 : 0;
  const canPrev = page > 0;
  const canNext = total != null ? (page + 1) * PAGE_SIZE < total : rows.length === PAGE_SIZE;

  return (
    <div className="w-full space-y-4 text-left">
      <SectionCard
        title="Order History"
        description="Orders your org submitted to manufacturers. On-hold rows show the hold reason and a Revise & Resubmit path."
      >
        <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
          <button
            type="button"
            className={
              viewMode === "open"
                ? "font-semibold text-indigo-800 dark:text-indigo-200"
                : "text-gray-600 underline dark:text-zinc-400"
            }
            onClick={() => setViewMode("open")}
          >
            Open / review
          </button>
          <span className="text-gray-400">|</span>
          <button
            type="button"
            className={
              viewMode === "all"
                ? "font-semibold text-indigo-800 dark:text-indigo-200"
                : "text-gray-600 underline dark:text-zinc-400"
            }
            onClick={() => setViewMode("all")}
          >
            All
          </button>
          <button
            type="button"
            className="text-indigo-700 underline dark:text-indigo-300"
            onClick={() => void load()}
            disabled={loading}
          >
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
        {error ? <p className="text-xs text-red-700 dark:text-red-300">{error}</p> : null}
        {!error && !loading && rows.length === 0 ? (
          <p className="text-xs text-gray-500 dark:text-zinc-500">No orders in this view.</p>
        ) : null}
        {rows.length > 0 ? (
          <ul className="space-y-2">
            {rows.map((row) => (
              <li
                key={row.id}
                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs dark:border-zinc-700 dark:bg-zinc-950/50"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-medium text-gray-900 dark:text-zinc-100">
                    {formatSalesRepOrderRowLabel(row, manufacturerNameByOrgId)}
                  </span>
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-700 dark:bg-zinc-800 dark:text-zinc-300">
                    {row.status.replace(/_/g, " ")}
                  </span>
                </div>
                {row.status === "on_hold" && row.hold_reason ? (
                  <p className="mt-1 text-amber-800 dark:text-amber-200">
                    Hold reason: {row.hold_reason}
                  </p>
                ) : null}
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <span className="font-mono text-[10px] text-gray-500 dark:text-zinc-500">
                    {row.id}
                  </span>
                  {row.status === "on_hold" ? (
                    <button type="button" className={ghostBtnClass} onClick={() => openResubmit(row)}>
                      Revise &amp; Resubmit
                    </button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        ) : null}
        {pageCount > 1 || canNext || canPrev ? (
          <div className="mt-3 flex items-center justify-between gap-2 text-xs text-gray-600 dark:text-zinc-400">
            <button
              type="button"
              className="underline disabled:opacity-40"
              disabled={!canPrev || loading}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              Previous
            </button>
            <span>
              Page {page + 1}
              {total != null ? ` of ${pageCount}` : ""}
            </span>
            <button
              type="button"
              className="underline disabled:opacity-40"
              disabled={!canNext || loading}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        ) : null}
      </SectionCard>

      {selected && resubmitDraft ? (
        <SectionCard
          title="Revise & Resubmit"
          description={`Intake ${selected.id.slice(0, 8)}… — edits merge into the same row and return it to pending review.`}
        >
          <DealerOrderResubmitForm
            selected={selected}
            draft={resubmitDraft}
            locations={locations}
            liveDiff={liveDiff}
            busy={resubmitBusy}
            error={resubmitError}
            success={resubmitSuccess}
            onDraftChange={setResubmitDraft}
            onSubmit={() => void submitResubmit()}
            onCancel={() => {
              setSelected(null);
              setResubmitDraft(null);
            }}
          />
        </SectionCard>
      ) : null}
    </div>
  );
}
